import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { 
  Box, Button, Typography, CircularProgress, Alert, Paper, Divider, Grid, Avatar
} from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import FreePlan from './plans/FreePlan';
import CombinedPremiumPlan from './plans/CombinedPremiumPlan';
import SubscriptionFAQ from './SubscriptionFAQ';
import { PLAN_TYPE } from '../constants/subscriptionTypes';

// Publishable key from env
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
// Base URL for Cloud Functions, must be set in .env (REACT_APP_FUNCTIONS_BASE_URL)
const functionsUrl = process.env.REACT_APP_FUNCTIONS_BASE_URL;
if (!functionsUrl) {
  console.warn('REACT_APP_FUNCTIONS_BASE_URL is not defined. Please set it in your .env file.');
}

export default function Account() {
  const { user } = useAuth();
  const { subscription, loading: subscriptionLoading, error: subscriptionError } = useSubscription();
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [annualLoading, setAnnualLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelMessage, setCancelMessage] = useState(null);

  const handleSubscribe = async (plan) => {
    // Set the appropriate loading state based on the plan
    if (plan === 'monthly') {
      setMonthlyLoading(true);
    } else if (plan === 'annual') {
      setAnnualLoading(true);
    }
    
    setMessage(null);
    try {
      const res = await fetch(`${functionsUrl}/createCheckoutSession`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan, userId: user.uid })
      });
      const data = await res.json();
      if (data.sessionId) {
        const stripe = await stripePromise;
        await stripe.redirectToCheckout({ sessionId: data.sessionId });
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to create session' });
      }
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    }
    
    // Reset the appropriate loading state
    if (plan === 'monthly') {
      setMonthlyLoading(false);
    } else if (plan === 'annual') {
      setAnnualLoading(false);
    }
  };

  const handleCancel = async () => {
    setCancelLoading(true);
    setCancelMessage(null);
    try {
      // Use email for user identification, consistent with syncUserByEmail approach
      const res = await fetch(`${functionsUrl}/cancelSubscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email })
      });
      const data = await res.json();
      if (data.success) {
        setCancelMessage({ type: 'success', text: 'Subscription will cancel at period end' });
      } else {
        setCancelMessage({ type: 'error', text: data.error || 'Cancel failed' });
      }
    } catch (e) {
      setCancelMessage({ type: 'error', text: e.message });
    }
    setCancelLoading(false);
  };

  // Helper function to format subscription type
  const formatSubscriptionType = (type) => {
    if (!type) return PLAN_TYPE.FREE;
    if (type === 'month' || type === PLAN_TYPE.MONTHLY) return 'Monthly';
    if (type === 'year' || type === PLAN_TYPE.YEARLY) return 'Annual';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  // Helper function to extract only the date part from timestamp strings
  const formatDateOnly = (dateTimeString) => {
    if (!dateTimeString) return '';
    // Extract just the date portion (e.g., "May 3, 2025" from "May 3, 2025 at 12:18:18 AM UTC+1")
    const datePart = dateTimeString.split(' at ')[0];
    return datePart;
  };

  const isSubscribed = subscription && (subscription.type === PLAN_TYPE.MONTHLY || subscription.type === PLAN_TYPE.YEARLY);

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Account Information Section */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>Account Information</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 2 }}>
          {user.photoURL && (
            <Avatar
              src={user.photoURL}
              alt={user.displayName}
              sx={{ width: 100, height: 100 }}
            />
          )}
          <Box>
            <Typography variant="body1"><strong>Name:</strong> {user.displayName}</Typography>
            <Typography variant="body1"><strong>Email:</strong> {user.email}</Typography>
            <Typography variant="body1"><strong>UID:</strong> {user.uid}</Typography>
          </Box>
        </Box>
      </Paper>

      <Divider />

      {/* Current Subscription Status */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>Your Subscription</Typography>
        
        {subscriptionLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : subscriptionError ? (
          <Alert severity="error">{subscriptionError}</Alert>
        ) : (
          <Box>
            {cancelMessage && <Alert severity={cancelMessage.type} sx={{ mb: 2 }}>{cancelMessage.text}</Alert>}
            <Typography variant="h6" sx={{ mt: 2, color: isSubscribed ? 'success.main' : 'text.secondary' }}>
              Current Plan: {formatSubscriptionType(subscription?.type)}
            </Typography>
            
            {subscription?.subscriptionDate && (
              <Typography variant="body1">
                Started: {subscription.subscriptionDate}
              </Typography>
            )}
            {subscription?.currentPlanValidTo && subscription.type !== PLAN_TYPE.FREE && (
              <Typography variant="body1">
                Current plan valid until {formatDateOnly(subscription.currentPlanValidTo)}
              </Typography>
            )}
            {subscription?.type !== PLAN_TYPE.FREE && !subscription?.canceledDate && subscription.nextPaymentDate && (
              <Typography variant="body1">
                Next payment: {formatDateOnly(subscription.nextPaymentDate)}
              </Typography>
            )}
            {subscription?.canceledDate && (
              <Typography variant="body1" color="warning.main">
                Next payment: Plan cancelled
              </Typography>
            )}
            {!subscription?.canceledDate && isSubscribed && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  color="error"
                  fullWidth
                  onClick={handleCancel}
                  disabled={cancelLoading}
                >
                  {cancelLoading ? <CircularProgress size={20} /> : 'Cancel Subscription'}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      <Divider />
      
      {/* Subscription Plans */}
      <Box>
        <Typography variant="h5" gutterBottom>Available Plans</Typography>
        {message && <Alert severity={message.type} sx={{ mb: 2 }}>{message.text}</Alert>}
        
        <Grid container spacing={3}>
          <FreePlan subscription={subscription} />
          <CombinedPremiumPlan 
            subscription={subscription} 
            monthlyLoading={monthlyLoading} 
            annualLoading={annualLoading} 
            handleSubscribe={handleSubscribe} 
          />
        </Grid>
      </Box>
      
      <Divider />
      
      {/* FAQ Section */}
      <SubscriptionFAQ />
    </Box>
  );
}
